package com.siriusxm.pia

import androidx.compose.runtime.*
import com.siriusxm.cmc.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.HashNavigator
import com.siriusxm.cmc.utils.Navigator
import com.siriusxm.cmc.utils.navigator
import com.siriusxm.cmc.views.Home
import com.siriusxm.mce.auth.browser.OAuthController
import com.siriusxm.pia.components.NotificationCenter
import com.siriusxm.pia.player.playerArtworkUrl
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import com.siriusxm.pia.views.account.UserAccountPage
import com.siriusxm.pia.views.chat.ChatApplication
import com.siriusxm.pia.views.mddb.MDDBApplication
import com.siriusxm.pia.views.news.newsPage
import com.siriusxm.pia.views.unifiedaggregator.AggregatorApplication
import com.siriusxm.pia.views.unifiedaggregator.GlobalAggregatorLoader
import com.siriusxm.unifiedcontent.cmc.SubscriberConnectionRequired
import com.siriusxm.unifiedcontent.console.subscriber.SubscriberConnection
import com.siriusxm.unifiedcontent.console.player.AudioPlayerState
import com.siriusxm.unifiedcontent.console.player.SdkCMCPlayer
import com.siriusxm.unifiedcontent.console.player.channelCutsLookup
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.Style
import kotlin.time.Duration

/**
 * The main application.
 */
object Application : Navigator {
    lateinit var configuration: Configuration
    lateinit var cognito: OAuthController

    // The application job and scope.
    private val appJob = SupervisorJob()
    private val exceptionHandler = CoroutineExceptionHandler { _, e ->
        console.error("Uncaught in app scope", e)
    }
    val scope: CoroutineScope = CoroutineScope(appJob + Dispatchers.Default + exceptionHandler)
    val notifications = NotificationCenter(scope)
    lateinit var container: CMCContainer

    // Created early to support GlobalAggregatorLoader initialization
    val aggregatorApp = AggregatorApplication()
    val chatApp = ChatApplication()

    /** Browser-side channel cuts lookup (Aggregator mapping + EPG), shared by every player built here. */
    private val channelCuts = channelCutsLookup(
        aggregator = { aggregatorApp.aggregator },
        epgApiUrl = { aggregatorApp.context.configuration.epgApiUrl },
        auth = { aggregatorApp.context },
    )

    /**
     * The long-lived headless player backing the global bar (UCA-1111). The bar shares this player's
     * controller (see [audioPlayer]) so entity/direct playback flows through the same
     * prepare→track→engine path as every app-created [com.siriusxm.pia.player.CMCPlayer]; the descriptor
     * → track mapping lives once, in [SdkCMCPlayer], with the host's CDN artwork
     * transform ([com.siriusxm.pia.player.playerArtworkUrl]) injected.
     * Lazy so [aggregatorApp] (whose `cmcService` it reads) is constructed first.
     */
    private val barPlayer: com.siriusxm.pia.player.CMCPlayer by lazy {
        SdkCMCPlayer(
            cmcService = { aggregatorApp.cmcService },
            artworkUrl = ::playerArtworkUrl,
            channelCuts = channelCuts,
            onRefreshFailure = { entityId, error ->
                if (error is SubscriberConnectionRequired) {
                    audioPlayer.controller.stop()
                    SubscriberConnection.request(entityId)
                }
            },
        )
    }

    /** The global player-bar state, backed by [barPlayer]'s controller so the bar and facade share one engine. */
    val audioPlayer: AudioPlayerState by lazy {
        AudioPlayerState(
            controller = barPlayer.controller,
            relatedEntityHref = { "#aggregator/entity/${encodeURIComponent(it.id)}" },
            resolveRelatedEntityName = { reference ->
                val sourceId = aggregatorApp.aggregator.getMapping(reference.id).sourceId
                val entity = aggregatorApp.aggregator.getEntityById(sourceId, fields = "name")
                    .asEntities()
                    .filter { it.type == reference.type.name }
                    .maxByOrNull { it.version }
                    ?: error("Related entity ${reference.id} was not found")
                entity.name ?: error("Related entity ${reference.id} has no name")
            },
        )
    }

    private val origin = window.location.origin


    val context: ApplicationContext by lazy {
        object : ApplicationContext, TokenAccess by cognito, CMCContainer by container {
            override val configuration: Configuration
                get() = Application.configuration

            // AVP capability discovery (UCA-1091). Reads the auth-check URL from config and the live
            // access token from cognito; fails closed when the URL is unset. Hide-only — the server
            // still enforces every gated operation.
            override val capabilities: Capabilities =
                AvpCapabilities(
                    authCheckApiUrl = Application.configuration.authCheckApiUrl,
                    tokenProvider = { cognito.accessToken }
                )

            override fun navigate(path: String) {
                navigator.navigate(path)
            }

            override fun notification(notification: Notification) {
                notifications.show(notification)
            }

            override fun dismissNotification(id: String) {
                notifications.dismiss(id)
            }

            override fun notification(
                type: NotificationType, message: String, description: String?,
                autoDismiss: Duration?
            ) {
                when (type) {
                    NotificationType.INFO -> notifications.info(message, description, autoDismiss)
                    NotificationType.ERROR -> notifications.error(message, description, autoDismiss)
                    NotificationType.WARNING -> notifications.error(message, description, autoDismiss)
                }
            }

            override val path: String
                get() = navigation.path

            override fun push(newPath: String, title: String?, render: Boolean) {
                navigation.push(newPath, title, render)
            }

            override val player: MediaPlayer = object : MediaPlayer {
                override val isActive: Boolean get() = this@Application.audioPlayer.track.value != null

                override fun playEntity(entityId: String) {
                    scope.launch { this@Application.playEntity(entityId) }
                }

                override fun play(
                    url: String,
                    title: String?,
                    subTitle: String?,
                    imageUrl: String?
                ) {
                    audioPlayer.play(url, title, subTitle, imageUrl)
                }

                override fun playDetails(
                    type: String,
                    url: String,
                    title: String?,
                    subTitle: String?,
                    imageUrl: String?,
                    durationMs: Long?,
                ) {
                    scope.launch {
                        this@Application.playDetails(type, url, title, subTitle, imageUrl, durationMs)
                    }
                }

                override fun close() {
                    this@Application.audioPlayer.controller.stop()
                }

                override fun createPlayer(): com.siriusxm.pia.player.CMCPlayer =
                    SdkCMCPlayer(
                        cmcService = { aggregatorApp.cmcService },
                        artworkUrl = ::playerArtworkUrl,
                        channelCuts = channelCuts,
                    )
            }
        }
    }

    /**
     * Play an entity on the global bar. The [barPlayer] facade owns the prepare→track→engine path
     * (server-side podcast enrichment, CDN artwork, credential refresh); the one bar-specific concern
     * that stays here is turning a failure into a UI toast. [barPlayer] throws (a library must not
     * raise toasts); this catches and reports once. Both entry points are fire-and-forget
     * (scope.launch for the in-app MediaPlayer, scope.promise for the container bridge), so rethrowing
     * would surface the same failure again as an uncaught coroutine error or unhandled rejection.
     */
    suspend fun playEntity(entityId: String) {
        try {
            SubscriberConnection.dismiss()
            barPlayer.playEntity(entityId)
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: SubscriberConnectionRequired) {
            SubscriberConnection.request(entityId)
        } catch (error: Throwable) {
            context.showError("Unable to play entity", error.message)
        }
    }

    /**
     * Play caller-supplied details on the global bar (no Aggregator lookup) — for content that cannot be
     * resolved through Aggregator, most notably an expired/archived AOD episode whose media is still in
     * the bucket. Delegates to the shared [barPlayer] facade exactly as [playEntity] does, catching its
     * failure to surface a single UI toast.
     */
    suspend fun playDetails(
        type: String,
        url: String,
        title: String?,
        subTitle: String?,
        imageUrl: String?,
        durationMs: Long?,
    ) {
        try {
            barPlayer.playDetails(type, url, title, subTitle, imageUrl, durationMs)
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (error: Throwable) {
            context.showError("Unable to play media", error.message)
        }
    }

    private val navigation: HashNavigator = HashNavigator {
        var applicationId by remember {
            mutableStateOf<String?>(null)
        }
        var application by remember {
            mutableStateOf<InstalledApplication?>(null)
        }

        LaunchedEffect(applicationId) {
            application = applicationId?.let {
                getApplication(it)
            }
        }

        Style(AppStylesheet)
        renderPage(this, it, notifications) { route ->
            route.switch {
                // Explicit home route (also the default when path is blank)
                select("home") {
                    Home()
                }
                // News feed page
                select("news") {
                    newsPage()
                }
                // User account page
                select("account") {
                    UserAccountPage()
                }
                // Chat interface — routable via direct URL, not shown in nav
                select("chat") {
                    var chatReady by remember { mutableStateOf(false) }
                    LaunchedEffect(Unit) {
                        chatApp.initialize(context)
                        chatReady = true
                    }
                    if (chatReady) {
                        chatApp.route(this)
                    }
                }
                select(Regex(".+")) {
                    val thisRoute = this
                    if (match == application?.id) {
                        application?.application?.route(thisRoute)
                    } else {
                        applicationId = match
                    }
                }
                default {
                    Home()
                }
            }
        }
    }

    init {
        navigator = navigation
    }

    private lateinit var applications: List<InstalledApplication>


    suspend fun run() {
        val scopes = listOf(
            "openid",
            "email",
            "SXM/unified-content",
            "Transcription/rest-api",
            "DynamicRadio/visibility-flags",
            "MDDBEventBus/rest-api",
            "UnifiedCatalogAPI/sxm:unified-catalog:entities:read",
            "Mountain/sxm:unified-catalog:entities:read",
            "EPG/sxm:unified-catalog:entities:read",
            "UnifiedCatalogAPI/sxm:unified-catalog:entities:publish",
            "UnifiedCatalogAPI/sxm:unified-catalog:management:read",
            "UnifiedCatalogAPI/sxm:unified-catalog:management:write",
            "UnifiedCatalogAPI/sxm:unified-catalog:backfill:start",
            "Sports/sxm:unified-catalog:entities:read",
            "CMC/sxm:unified-catalog:entities:read",
            "ArtistStation/read"
        )

        configuration = config()
        val pkceEnabled = configuration.pkceEnabled.toBooleanStrictOrNull() ?: false
        cognito = OAuthController(
            configuration.authUrl,
            configuration.clientId,
            origin,
            "sxm",
            scopes.joinToString(" "),
            pkceEnabled
        )
        cognito.init()

        configureContainer()

        // Initialize early aggregator loading when logged in.
        // This starts loading aggregator data in the background immediately after auth,
        // so it's ready when the user navigates to the aggregator section.
        if (cognito.isLoggedIn()) {
            // The global player bar reads aggregatorApp's context (for cmcService and channel
            // cuts) on every play. Without this it is set only when the user first opens the
            // aggregator section, so playing from an embedded app such as EPG beforehand threw
            // "lateinit property context has not been initialized" (UCA-1279).
            aggregatorApp.initialize(context)
            GlobalAggregatorLoader.initialize(aggregatorApp, context, scope)
            GlobalAggregatorLoader.startLoading()
        }

        if (cognito.isLoggedIn()) {
            loadApplicationCapabilities(context, configuration.applications.orEmpty())
        }
        initApplications(context, configuration.applications.orEmpty())
        if (cognito.isLoggedIn()) {
            navigation.run()
        }
    }

    /**
     * Configure the container to allow embedded iframe application
     * access to certain container fields.
     */
    private fun configureContainer() {
        container = ApplicationContainer(this)
        window.asDynamic()["cmc"] = container
    }

    private suspend fun initApplications(
        context: ApplicationContext,
        applications: List<ApplicationDescriptor>
    ) {
        this.applications = applications.filter {
            hasApplicationAccess(it)
        }.mapNotNull { descriptor ->
            when (descriptor.path) {
                "aggregator" -> aggregatorApp
                "mddb" -> MDDBApplication()
                else -> {
                    if (descriptor.source != null) {
                        IFrameApplication(descriptor)
                    } else {
                        null
                    }
                }
            }?.let { app ->
                InstalledApplication(descriptor, app)
            }
        }

        this.applications.forEach {
            if (it.descriptor.initOnStartup) {
                it.initialize(context)
            }
        }
    }

    /**
     * Load configuration and applications.
     */
    private suspend fun config(): Configuration {
        return coroutineScope {
            val httpClient = HttpClient {
                install(ContentNegotiation) {
                    json(Json { isLenient = true; ignoreUnknownKeys = true })
                }
            }
            val configJob = async {
                httpClient.get("$origin/config.json").body<Configuration>()
            }
            val config = configJob.await()
            if (config.cmcApiUrl == null) {
                config.copy(cmcApiUrl = window.location.origin + "/cmc")
            } else {
                config
            }
        }
    }

    /**
     * Get the viewer of the application.
     */
    val viewer: Viewer by lazy {
        Viewer(cognito.idToken, cognito.accessToken, cognito.accessToken)
    }

    /**
     * Resolves, in one batched auth-check call, the UCP capability each configured application
     * allows access by (UCA-1181). Results are cached by [Capabilities]; if the check fails the
     * capabilities read as denied, so access falls back to [ApplicationDescriptor.requiredGroup].
     */
    private suspend fun loadApplicationCapabilities(
        context: ApplicationContext,
        applications: List<ApplicationDescriptor>
    ) {
        val queries = applications.mapNotNull { it.capabilityQuery() }
        if (queries.isNotEmpty()) {
            context.capabilities.refresh(queries)
        }
    }

    /**
     * Whether the viewer may use an application: no restrictions are configured, the viewer is in
     * one of its required groups, or the viewer holds the UCP capability it allows access by.
     */
    fun hasApplicationAccess(descriptor: ApplicationDescriptor): Boolean =
        applicationAccessAllowed(
            descriptor,
            isInGroup = { viewer.isInGroup(it) },
            isCapabilityAllowed = { context.capabilities.isAllowed(it) }
        )

    /**
     * Get the initialized application
     */
    suspend fun getApplication(key: String): InstalledApplication? {
        return applications.find {
            it.descriptor.path == key && hasApplicationAccess(it.descriptor)
        }?.also {
            it.initialize(context)
        }
    }

    override fun navigate(path: String) {
        navigation.navigate(path)
    }

    override fun push(newPath: String, title: String?, render: Boolean) {
        navigation.push(newPath, title, render)
    }

    /**
     * Navigate to [path] and stash [title] for the resulting history entry. The
     * label is applied via document.title once the iframe (or container) lands
     * on the new page; pushState's title argument is ignored by browsers.
     * Falls back to plain navigate when [title] is blank.
     */
    fun navigateWithTitle(path: String, title: String) {
        pendingTitle = title.takeIf { it.isNotBlank() }
        navigation.navigate(path)
    }

    /**
     * One-shot title to apply to the next post-navigate document.title write.
     * Consumed (set to null) by IFrameApplication once it has updated the title.
     */
    var pendingTitle: String? = null
}

class InstalledApplication(
    val descriptor: ApplicationDescriptor,
    val application: ConsoleApplication
) {
    val id: String = descriptor.id ?: descriptor.path

    private var initialized: Boolean = false

    suspend fun initialize(context: ApplicationContext) {
        if (!initialized) {
            application.initialize(context)
        }
    }
}
